import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';

/**
 * Protected route component that redirects to login if user is not authenticated
 * Also handles session validation and token refresh when accessing protected routes
 */
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { 
    isAuthenticated, 
    tokenValidationComplete, 
    loading, 
    user, 
    refreshTokens,
    error
  } = useAuth();
  const location = useLocation();
  
  // Validate session when accessing protected route
  useEffect(() => {
    // Only proceed if properly authenticated
    if (isAuthenticated && user) {
      // Use setTimeout to avoid possible render loop with state updates
      const refreshTimeout = setTimeout(() => {
        // Only refresh tokens if it's been a while since the last refresh
        // Pass a flag to inform refreshTokens this is a route-based refresh
        refreshTokens({ routeAccess: true }).catch(err => {
          console.error('Failed to refresh tokens on route access:', err);
        });
      }, 0);
      
      return () => clearTimeout(refreshTimeout);
    }
  }, [location.key]); // Only depend on the location key instead of pathname
  
  // If we're still checking the token status, display a loading state
  if (!tokenValidationComplete || loading) {
    return (
      <div className="auth-loading-container">
        <div className="auth-loading-spinner"></div>
        <p>Verifying access...</p>
      </div>
    );
  }
  
  // If there was an authentication error
  if (error) {
    return (
      <div className="auth-error-container">
        <p>Authentication error: {error}</p>
        <p>Please <Navigate to="/login" state={{ from: location }} replace>log in</Navigate> again.</p>
      </div>
    );
  }
  
  // If the user isn't authenticated, redirect to the login page
  if (!isAuthenticated) {
    // Save the current location to redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If specific roles are required for this route
  if (requiredRoles.length > 0 && user) {
    // Check if user has the required role(s)
    const userRoles = user.roles || [];
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    
    // If user doesn't have the required role, show unauthorized page or redirect
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }
  
  // If the user is authenticated and has the required roles (if any), render the protected content
  return children;
};

export default ProtectedRoute;